import React, { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { Info } from "lucide-react";
import "@/App.css";

import { useDevMode } from "./lib/anchors";
import { preloadMethods, clearMethodsCache } from "./lib/methodsCache";
import { AuthProvider, useAuth, isRememberedDevice, associationsPrefetch, isPlatformAdmin, isAdmin, isViewOnly } from "./contexts/AuthContext";
import { SettingsProvider } from "./contexts/SettingsContext";
import LoginScreen from "./screens/LoginScreen";
import LandingScreen from "./screens/LandingScreen";
import IllustrationsScreen from "./screens/IllustrationsScreen";
import EnrollScreen from "./screens/EnrollScreen";
import SubscribeInviteScreen, { PENDING_SUBSCRIBE_KEY } from "./screens/SubscribeInviteScreen";
import TopBar, { ADMIN_SUBSCREENS } from "./components/TopBar";
import ControlsStrip from "./components/ControlsStrip";
import ReportModal from "./components/ReportModal";
import CalendarModal from "./components/CalendarModal";
import ScenarioHistoryModal from "./components/ScenarioHistoryModal";
import AttachmentsModal from "./components/AttachmentsModal";
import { invalidateAttachmentHoverCache } from "./components/AttachmentHoverPreview";
import StatementOfPurposeModal from "./components/StatementOfPurposeModal";
import ChangePasswordModal from "./components/ChangePasswordModal";
import EditNameModal from "./components/EditNameModal";
import { ForgotPasswordModal, ResetPasswordTokenModal } from "./components/ForgotPasswordModal";
import MaintenancePage from "./screens/MaintenancePage";
import MaintenanceBanner from "./components/MaintenanceBanner";
import SessionExpiringToast from "./components/SessionExpiringToast";
import { useSettings } from "./contexts/SettingsContext";
import { useSessionIdle } from "./hooks/useSessionIdle";
import { useActivityHeartbeat } from "./hooks/useActivityHeartbeat";
import DashboardScreen from "./screens/DashboardScreen";
import AssociationScreen from "./screens/AssociationScreen";
import ElementsScreen from "./screens/ElementsScreen";
import ProjectionsScreen from "./screens/ProjectionsScreen";
import ContributionsScreen from "./screens/ContributionsScreen";
import ResourcesScreen from "./screens/ResourcesScreen";
import DevelopmentScreen from "./screens/DevelopmentScreen";
import SupportScreen from "./screens/SupportScreen";
import FirmsScreen from "./screens/FirmsScreen";
import UsersScreen from "./screens/UsersScreen";
import AdminScreen from "./screens/AdminScreen";
import ReportsScreen from "./screens/ReportsScreen";
import AuditTrailScreen from "./screens/AuditTrailScreen";
import EmailLogScreen from "./screens/EmailLogScreen";
import SubscriptionsScreen from "./screens/SubscriptionsScreen";
import AssociationsAdminScreen from "./screens/AssociationsAdminScreen";
import ReserveBudgetReport from "./screens/ReserveBudgetReport";
import { Toaster, toast } from "sonner";
import SetGoToScenarioModal from "./components/SetGoToScenarioModal";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function Shell({ platformState }) {
  const { settings: platformSettings } = useSettings();
  // 2026-02-27 (Dave) — pull `user` so the landing-association picker
  // can honor `user.default_association_id` from /auth/me.
  const { user, refresh: refreshAuth } = useAuth();
  const [devMode, disableDevMode] = useDevMode();
  const [associations, setAssociations] = useState([]);
  // 2026-08-04 (Dave bug) — track whether the initial associations fetch has
  // completed so downstream guards (TopBar's "clear currentAid when firm has
  // zero associations" effect, and the empty-firm-state placeholder) don't
  // fire during the transient [] state right after login, briefly flashing
  // "No associations under this firm" before the real data arrives.
  const [associationsLoaded, setAssociationsLoaded] = useState(false);
  const [currentAid, setCurrentAid] = useState(null);
  const [association, setAssociation] = useState(null);
  const [variables, setVariables] = useState(null);
  const [originalVariables, setOriginalVariables] = useState(null);
  const [calc, setCalc] = useState(null);
  // Baseline calc — always the pristine engine result for the plan-year's
  // baseline scenario. Kept alongside `calc` (which follows the active
  // scenario) so the Elements screen can show a Baseline-vs-Scenario diff.
  const [baselineCalc, setBaselineCalc] = useState(null);
  const [calcTimestamp, setCalcTimestamp] = useState(null);
  const setCalcWithStamp = (data) => {
    setCalc(data);
    setCalcTimestamp(new Date());
  };
  const [running, setRunning] = useState(false);
  const [error, setError] = useState(null);
  const [showCalendar, setShowCalendar] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [scenarios, setScenarios] = useState([]);
  const [activeScenarioId, setActiveScenarioId] = useState(null);
  // 2026-03-01 (Dave) — Persist the last Go-To scenario per association
  // so a browser restart lands the user right back on the scenario they
  // were on. `null` and the baseline-scenario-id both clear the entry
  // (baseline is the natural default, so no need to store it).
  //
  // 2026-03-02 race fix — Effects run in declaration order on every
  // render, so when `currentAid` changes to a new association, this
  // write-back effect fires BEFORE the initial-fetch effect resolves.
  // At that moment `association` still holds the PREVIOUS association's
  // bundle (aid mismatch), and both `activeScenarioId` and
  // `association.baseline_scenario_id` still equal that old baseline id
  // — so the `else` branch used to wipe the NEW aid's LS key before
  // the fetch effect could read it. The `association?.aid === currentAid`
  // gate below is the sync barrier that keeps write-back off until the
  // bundle for the current aid has actually landed.
  // 2026-10-20 (Dave) — The coral Go-To star (server-side) is the single source of
  // truth for which scenario loads; the old per-browser "last viewed" memory is retired.

  // 2026-03-02 (Dave) — Persist the last-visited association aid so a
  // browser restart lands the user on the association they were on
  // (not just the server-side default_association_id). Combined with
  // the Go-To write-back above, a full reload returns the user to the
  // exact scenario they were working in.
  useEffect(() => {
    if (!currentAid) return;
    try { window.localStorage.setItem("ureserve.lastAid", currentAid); }
    catch { /* private-mode / disabled storage → non-blocking */ }
  }, [currentAid]);
  // Slice E2 — SetGoToScenarioModal state. Replaces the native window.confirm
  // fired after a batch element edit creates an auto-scenario. Shape:
  //   { scenarioId, scenarioName, bundle }  (bundle = fresh AssociationOut
  //   already fetched from the batch endpoint; used by the Activate path).
  const [gotoPrompt, setGotoPrompt] = useState(null);
  // 2026-02-28 (Dave) — post-save inline rename banner. Populated when a
  // batch save creates an auto-scenario; cleared when the user submits a
  // rename or dismisses. Shape: { scenarioId, currentName, aid }.
  const [renameNudge, setRenameNudge] = useState(null);
  const [displayHorizon, setDisplayHorizon] = useState(30);
  // 2026-03-02 (Dave) — Contributions-tab view toggle. `false` renders
  // the year-by-year detail rows (default). `true` swaps in the
  // Comparative Analysis chart as a positioned overlay over the year
  // area (underlying rows peek at the edges). Toggle lives in TopBar
  // next to the user identity pill and only surfaces on Contributions.
  const [contribChartView, setContribChartView] = useState(true);
  // 2026-03-01 (Dave) — Elements-tab live-preview loading state, hoisted
  // from ElementsScreen so the ControlsStrip can render the "Recalculating…"
  // pill in the blue header row above (instead of inside the Elements
  // toolbar where it shifted the Save button on every tick).
  const [elementsRecalcActive, setElementsRecalcActive] = useState(false);
  // 2026-03-01 (Dave) — Association-level attachments (paperclip in the
  // blue ControlsStrip header). State holds:
  //   * modal open flag
  //   * count for the pill's `(N)` badge, kept in sync with the modal
  const [showAssocAttachments, setShowAssocAttachments] = useState(false);
  const [assocAttachmentCount, setAssocAttachmentCount] = useState(0);
  // 2026-03-04 (Dave · Option A) — In-place Reserve Budget Report
  // overlay. `showReportModal` is toggled from the ControlsStrip's
  // "Reserve Budget Report" pill; the modal itself lives near the
  // bottom of Shell alongside the other overlays.
  const [showReportModal, setShowReportModal] = useState(false);
  const refreshAssocAttachmentCount = useCallback(async () => {
    if (!currentAid) { setAssocAttachmentCount(0); return; }
    try {
      const r = await axios.get(`${API}/associations/${currentAid}/attachments`);
      setAssocAttachmentCount(Array.isArray(r.data) ? r.data.length : 0);
    } catch { /* non-blocking */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentAid]);
  useEffect(() => {
    // 2026-03-04 (Dave · initial-load perf) — Attachment count is
    // ONLY consumed by ControlsStrip (Association/Elements/
    // Projections tabs). Dashboard never renders it. Firing this
    // request eagerly on `currentAid` change made it compete with
    // the critical /associations/{aid} + /calculate calls on cold
    // login (measured +1.3 s of parallel wall-clock work on the
    // Dashboard render path). Defer to browser idle instead so the
    // critical path finishes first; the badge count populates
    // milliseconds later, still well before the user could
    // navigate off Dashboard.
    let handle = null;
    const fire = () => { refreshAssocAttachmentCount(); handle = null; };
    if (typeof window !== "undefined" && "requestIdleCallback" in window) {
      // 2 s max wait so the count refreshes even on a busy tab.
      handle = window.requestIdleCallback(fire, { timeout: 2000 });
      return () => { if (handle && "cancelIdleCallback" in window) window.cancelIdleCallback(handle); };
    }
    // Fallback (Safari doesn't ship requestIdleCallback yet).
    handle = setTimeout(fire, 600);
    return () => { if (handle) clearTimeout(handle); };
  }, [refreshAssocAttachmentCount]);

  // Slice B (Dave 2026-02-17) — In-app prior-year browsing.
  // `viewingPlanYearId` is null when the user is viewing the currently
  // active plan year (the default). When they pick a closed year from
  // the TopBar Plan-Year selector, this holds that PY's UUID and the
  // axios interceptor below transparently appends `?plan_year_id=<id>`
  // to every /associations/{aid}[...] read so all screens re-fetch
  // that year's data. Writes still gate on the backend guard-rail;
  // the UI additionally hides most edit affordances via
  // `isViewingPriorYear`. Cleared automatically when the association
  // changes (setCurrentAid effect below).
  const viewingPlanYearIdRef = useRef(null);
  const [viewingPlanYearId, _setViewingPlanYearId] = useState(null);
  const setViewingPlanYearId = useCallback((id) => {
    viewingPlanYearIdRef.current = id || null;
    _setViewingPlanYearId(id || null);
  }, []);
  // Register one axios request interceptor for the whole app. Uses a
  // ref so state changes are picked up without re-registering.
  useEffect(() => {
    const iid = axios.interceptors.request.use((config) => {
      const pyId = viewingPlanYearIdRef.current;
      if (!pyId) return config;
      const url = String(config.url || "");
      // Only inject on /api/associations/{aid}[/scenarios|/calculate|/methods]
      // — not on POST /plan-years/{id}/reactivate (which needs no param).
      if (!/\/api\/associations\/[^/?]+(\/(scenarios|calculate|methods))?(?:\?|$)/.test(url)) {
        return config;
      }
      try {
        const u = new URL(url, window.location.origin);
        if (u.searchParams.has("plan_year_id")) return config;
        u.searchParams.set("plan_year_id", pyId);
        config.url = u.toString();
      } catch { /* leave as-is on parse failure */ }
      return config;
    });
    return () => axios.interceptors.request.eject(iid);
  }, []);

  // Slice B polish v2 (Dave 2026-02-17) — Coral Reactivate Tooltip.
  // Because dimmed controls in `main.readonly-view` set
  // `pointer-events: none`, native browser tooltips (title attribute)
  // never fire on them. Instead we track `mousemove` at document level
  // and use `elementsFromPoint()` (plural — pierces pointer-events:
  // none) to detect hover over any dimmed control. When we see one,
  // a small floating coral tooltip renders near the cursor telling
  // the user why the control isn't working and what to do about it.
  //
  // Only active when `viewingPlanYearId` is set — no cost on the
  // active-year path. Cleanup unmounts the tooltip node and the
  // listener.
  useEffect(() => {
    if (!viewingPlanYearId) return undefined;
    const SEL =
      'input[type="range"], input[type="number"], input[type="text"],' +
      ' input[type="date"], textarea, select, [contenteditable="true"],' +
      ' button[data-write-action], [data-write-action="true"],' +
      ' [role="slider"]';
    const tip = document.createElement("div");
    tip.setAttribute("data-testid", "readonly-tooltip");
    tip.textContent = "Closed year — Reactivate to edit";
    Object.assign(tip.style, {
      position: "fixed",
      zIndex: "9999",
      background: "#EA6A47",
      color: "white",
      fontFamily: '"Source Sans 3", "Helvetica", sans-serif',
      fontSize: "11px",
      fontWeight: "600",
      padding: "5px 10px",
      borderRadius: "4px",
      boxShadow: "0 2px 8px rgba(15,42,68,0.28)",
      pointerEvents: "none",
      opacity: "0",
      transform: "translateY(4px)",
      transition: "opacity 120ms ease-out, transform 120ms ease-out",
      whiteSpace: "nowrap",
      letterSpacing: "0.01em",
    });
    document.body.appendChild(tip);

    const isReadonlyControl = (el) => {
      if (!el || !el.matches) return false;
      // Element must be inside main.readonly-view (guards against
      // matching top-bar controls or drawer contents).
      const inRO = el.closest("main.readonly-view");
      if (!inRO) return false;
      // Direct hit: the element itself or an ancestor matches an
      // editable selector.
      if (el.matches(SEL) || el.closest?.(SEL)) return true;
      // Shadcn/Radix slider wrapper — the [role="slider"] thumb is a
      // ~14px dot; the hovered element is usually the track div. We
      // still want to help the user, so check for a matching
      // descendant.
      if (
        el.querySelector?.(
          '[role="slider"], input[type="range"], input[type="number"]'
        )
      ) {
        return true;
      }
      return false;
    };

    let raf = 0;
    const onMove = (e) => {
      if (raf) return;
      raf = requestAnimationFrame(() => {
        raf = 0;
        // `elementsFromPoint` (plural) pierces pointer-events:none —
        // the whole reason we need this JS instead of a CSS :hover.
        const stack = document.elementsFromPoint(e.clientX, e.clientY) || [];
        const hit = stack.find((el) => isReadonlyControl(el));
        if (hit) {
          // Nudge to bottom-right of cursor; flip to top-left if we're
          // near the right or bottom edge.
          const vw = window.innerWidth;
          const vh = window.innerHeight;
          const tw = tip.offsetWidth || 200;
          const th = tip.offsetHeight || 28;
          let x = e.clientX + 12;
          let y = e.clientY + 14;
          if (x + tw > vw - 8) x = e.clientX - tw - 12;
          if (y + th > vh - 8) y = e.clientY - th - 12;
          tip.style.left = `${x}px`;
          tip.style.top = `${y}px`;
          tip.style.opacity = "0.98";
          tip.style.transform = "translateY(0)";
        } else {
          tip.style.opacity = "0";
          tip.style.transform = "translateY(4px)";
        }
      });
    };
    const onLeave = () => {
      tip.style.opacity = "0";
      tip.style.transform = "translateY(4px)";
    };

    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseleave", onLeave);
    window.addEventListener("scroll", onLeave, true);

    return () => {
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseleave", onLeave);
      window.removeEventListener("scroll", onLeave, true);
      if (raf) cancelAnimationFrame(raf);
      tip.remove();
    };
  }, [viewingPlanYearId]);
  // Durable deep-link: read `#<screen>` on mount, e.g. `#testing` loads the
  // Testing Bench directly. We normalize case and re-sync the hash any time
  // the current screen changes so a bookmark or share link always works.
  //
  // 2026-02-19 (Dave) — CARVE-OUTS: never normalize `#login?invite=…` or
  // `#reset?token=…`. Those hashes are consumed by AppGate (the invite
  // force-logout + the reset-with-token modal) and must survive Shell's
  // initial mount unchanged. Without these guards Shell would rewrite
  // the hash to `#dashboard` before the AppGate effects could read it.
  //
  // 2026-02-28 (Dave) — Always land the user on Dashboard when the
  // app boots.  Safari's "reopen last closed tab" restores the URL
  // hash so the app previously deep-linked into whichever tab the
  // user had last, but Chrome doesn't — the inconsistency made the
  // app feel unpredictable.  Enforcing Dashboard on every open (bar
  // the auth carve-outs) keeps the experience identical across
  // browsers.  The `useEffect` below will rewrite `location.hash`
  // to `#dashboard` on mount so the URL matches the visible tab.
  // 2026-03-04 (Dave bug — pill gone after PDF report flow) —
  // Persist `currentScreen` in sessionStorage so that if `Shell` ever
  // remounts (e.g., `AppGate` short-circuits to render the standalone
  // Reserve Budget Report when the hash flips to `#report/…`, then
  // reverts), the tab the user was on comes back automatically
  // instead of snapping to Dashboard. Chrome and Safari both open
  // the report via `<a target="_blank">` (new tab, current tab
  // untouched), but embedded preview iframes (e.g., emergent.sh's
  // Preview panel) can navigate the SAME frame despite `_blank`,
  // which unmounts Shell and loses state. sessionStorage survives
  // the round-trip and clears when the tab is closed.
  //
  // Restore is GATED to internal remounts only — on a real page
  // reload / navigation (`performance.getEntriesByType('navigation')`
  // reports "reload" / "navigate" / "back_forward"), we honour the
  // existing "always land on Dashboard on fresh open" design intent
  // (see comment block above). Internal remounts have no navigation
  // entry, so they cleanly opt in to state restoration.
  const initialScreen = React.useMemo(() => {
    const raw = window.location.hash || "";
    if (raw.startsWith("#login?invite=") || raw.startsWith("#reset?token=") || raw.startsWith("#report/")) {
      return "Dashboard";
    }
    // Only restore across an in-tab component remount, NOT across a
    // real navigation/reload. `performance.now()` — if it's high
    // enough that a real navigation entry has already been recorded
    // for THIS mount, we treat the mount as a full page load and
    // ignore the saved screen. The Shell remount that follows a
    // report-hash short-circuit has NO fresh navigation entry, so
    // performance.now() at that moment reflects seconds-into-session
    // and the saved screen is honoured.
    try {
      const navEntry = (window.performance?.getEntriesByType?.("navigation") || [])[0];
      // startTime is 0 for the initial page nav; a genuine reload/
      // navigate has this within a few hundred ms of `performance.now()`.
      const isFreshPageLoad = navEntry && (performance.now() - navEntry.startTime) < 5000;
      if (isFreshPageLoad) return "Dashboard";
      // Keyed per user so a different account signing in on this tab starts on Dashboard.
      const saved = window.sessionStorage.getItem(`ureserve.currentScreen.${user?.user_id || ""}`);
      const KNOWN = ["Dashboard", "Association", "Elements", "Projections", "Contributions", "Illustrations", "Support", "Admin", "Firms", "Users", "Audit"];
      if (saved && KNOWN.includes(saved)) return saved;
    } catch { /* sessionStorage / performance disabled — fall through */ }
    return "Dashboard";
  }, []);
  const [currentScreen, setCurrentScreen] = useState(initialScreen);
  useEffect(() => {
    try { window.sessionStorage.setItem(`ureserve.currentScreen.${user?.user_id || ""}`, currentScreen); } catch { /* ignore */ }
  }, [currentScreen, user?.user_id]);
  useEffect(() => {
    const raw = window.location.hash || "";
    // Do NOT stomp the invite / reset / report hash mid-flow — those
    // are read by AppGate to bootstrap a special flow. Rewriting them
    // here silently breaks force-logout and standalone-report tabs.
    if (raw.startsWith("#login?invite=") || raw.startsWith("#reset?token=") || raw.startsWith("#report/")) {
      return;
    }
    const next = `#${currentScreen.toLowerCase()}`;
    if (raw !== next) {
      window.history.replaceState(null, "", `${window.location.pathname}${window.location.search}${next}`);
    }
  }, [currentScreen]);

  // 2026-03-02 (Dave) — Silently dismiss the Comparative Analysis
  // chart whenever the user navigates OFF the Contributions tab, so
  // returning to it later lands on the Comparative Analysis view
  // (2026-09-13: promoted to default per Dave) instead of a Detailed
  // Data View the user may have last left open.
  useEffect(() => {
    if (currentScreen !== "Contributions" && !contribChartView) {
      setContribChartView(true);
    }
  }, [currentScreen, contribChartView]);

  // Slice 6 (Dave 2026-03-09 · Severed Architecture) — Mothball redirect.
  // When the user picks a closed plan year while looking at an edit-focused
  // tab (Elements or Association), those tabs are hidden by TopBar. Kick
  // the current screen back to Dashboard so we don't strand the user on a
  // rendered-but-untabbed screen.
  useEffect(() => {
    if (viewingPlanYearId && (currentScreen === "Elements" || currentScreen === "Association")) {
      setCurrentScreen("Dashboard");
    }
  }, [viewingPlanYearId, currentScreen]);

  // 2026-03-04 (Dave) — Snap off a hidden tab. When Version Content
  // switches (or a tier flip on the current association) make the
  // current screen unreachable — because either the tier's `<tab>_tab_*`
  // key is off, or the tab was removed from the nav for any reason —
  // auto-route the user to Dashboard so they don't stare at content
  // they can no longer navigate back to. Mirrors the TopBar `visibleNav`
  // rule and only touches the three tier-gated tabs; all others (
  // Dashboard/Association/Support/Admin) are always allowed.
  useEffect(() => {
    if (!platformSettings) return;
    const tier = ((association && association.tier) || "pro").toLowerCase();
    const suffix = tier === "basic" ? "basic" : "pro";
    const gate = {
      Contributions: `contributions_tab_${suffix}`,
      Elements:      `elements_tab_${suffix}`,
      Projections:   `projections_tab_${suffix}`,
    };
    const key = gate[currentScreen];
    if (!key) return;
    if (Number(platformSettings[key] ?? 1) !== 1) {
      setCurrentScreen("Dashboard");
    }
  }, [platformSettings, association, currentScreen]);

  // 2026-02-19 (Dave) — Idle-back-to-Dashboard. When the user is on
  // ANY tab other than Dashboard and doesn't touch the app for N
  // seconds (platform_settings.idle_dashboard_snapback_seconds,
  // default 30, range 10..600), snap them back to the Dashboard.
  // Distinct from the authentication idle-logout (which fires much
  // later): this is a pure UX default — "when in doubt, go home".
  // Dashboard itself is exempt (no timer while already there).
  //
  // Reset on any user interaction: mousemove, keydown, click, scroll,
  // touch. Uses `{passive: true}` for the listeners so we don't
  // interfere with scroll performance.
  const idleSnapbackSecs = Number(platformSettings?.idle_dashboard_snapback_seconds ?? 30);
  useEffect(() => {
    // Elements tab is exempt from idle snapback (Dave 2026-11-04).
    if (currentScreen === "Dashboard" || currentScreen === "Elements") return undefined;
    const IDLE_MS = Math.max(10, idleSnapbackSecs) * 1000;
    let timerId = null;
    const arm = () => {
      if (timerId) clearTimeout(timerId);
      // 2026-03-04 (Dave bug) — Skip arming while the tab is hidden.
      // The most common trigger of this timer used to be: user opens
      // the Reserve Budget Report in a new tab, reads it for > 30 s,
      // comes back to the original tab and finds themselves snapped
      // to Dashboard (having never touched the original tab). That
      // silent state flip cascaded into a brief post-nav rerender
      // race that could leave the Contributions toggle pill
      // temporarily un-rendered. Pausing the countdown while
      // `document.hidden` is true means only ACTIVE idle time counts.
      if (typeof document !== "undefined" && document.hidden) return;
      // Cascading snapback: Admin sub-views → Admin → Dashboard.
      const parent = ADMIN_SUBSCREENS.includes(currentScreen) ? "Admin" : "Dashboard";
      timerId = setTimeout(() => {
        // Hold while the Platform Settings modal is open (it closes first, then this re-arms).
        if (document.body.dataset.idleHold || document.body.dataset.idleExempt) { arm(); return; }
        setCurrentScreen(parent);
      }, IDLE_MS);
    };
    arm();
    const events = ["mousemove", "keydown", "click", "scroll", "touchstart", "ureserve:idle-step"];
    events.forEach((ev) => window.addEventListener(ev, arm, { passive: true }));
    // Re-arm / cancel on visibility change so the moment the tab goes
    // to the background the pending timer is scrapped, and when it
    // comes back to the foreground a fresh countdown starts.
    const onVis = () => {
      if (document.hidden) {
        if (timerId) { clearTimeout(timerId); timerId = null; }
      } else {
        arm();
      }
    };
    document.addEventListener("visibilitychange", onVis);
    return () => {
      if (timerId) clearTimeout(timerId);
      events.forEach((ev) => window.removeEventListener(ev, arm));
      document.removeEventListener("visibilitychange", onVis);
    };
  }, [currentScreen, idleSnapbackSecs]);


  const debounceRef = useRef(null);
  const requestSeq = useRef(0);

  // Slice 5g follow-up (2026-02-25) — extracted so it can be re-run on
  // demand (e.g. after an Association rename in FirmsScreen). We keep it
  // idempotent w.r.t. `currentAid`: only pick a default when no association
  // is selected yet, so a refresh doesn't yank the user to a different aid.
  //
  // 2026-02-27 (Dave) — landing-association order:
  //   1. The user's saved `default_association_id` (from /auth/me), if
  //      they still have access to it.
  //   2. Otherwise the first accessible association in the list.
  // The legacy "prefer Lakes" scaffold is removed — it was a test seed
  // that shouldn't have been shipping preference for real users.
  const loadAssociations = useCallback(async () => {
    try {
      // 2026-02-27 (Dave · perf C) — First render on cold login: consume
      // the associations request that AuthContext kicked off in parallel
      // with /auth/me. Saves one round-trip. `null` means the prefetch
      // errored (rare); we fall through to a fresh axios.get so nothing
      // relies on the cache being present.
      let data = null;
      if (associationsPrefetch.promise) {
        data = await associationsPrefetch.promise;
        // One-shot cache — subsequent reloads (e.g. after firm rename)
        // must fetch fresh so the list reflects the mutation.
        associationsPrefetch.promise = null;
      }
      if (!data) {
        const res = await axios.get(`${API}/associations`);
        data = res.data;
      }
      setAssociations(data);
      // 2026-03-03 (Dave bug) — Priority order fixed. Previously
      // localStorage `ureserve.lastAid` overrode the user's saved
      // `default_association_id`, so the star-marked default on the
      // Association tab wasn't respected when the app was reopened.
      // New order:
      //   1. `default_association_id` (star preference set by the user) —
      //      always wins when the aid is still accessible.
      //   2. `ureserve.lastAid` (localStorage) — used only when the user
      //      hasn't pinned a default. Preserves the mid-session "come
      //      back to where I was" convenience.
      //   3. First accessible association.
      let lastAid = null;
      try { lastAid = window.localStorage.getItem("ureserve.lastAid"); }
      catch { /* private-mode */ }
      const preferredAid = user?.default_association_id || null;
      const pickDefault = () => {
        if (preferredAid) {
          const hit = data.find((a) => a.aid === preferredAid);
          if (hit) return hit;
        }
        if (lastAid) {
          const hit = data.find((a) => a.aid === lastAid);
          if (hit) return hit;
        }
        return data[0];
      };
      if (!data.length) {
        // No accessible associations — clear any stale aid so downstream
        // screens don't fire requests that would 403.
        if (currentAid) setCurrentAid(null);
      } else if (!currentAid) {
        setCurrentAid(pickDefault().aid);
      } else if (!data.some((a) => a.aid === currentAid)) {
        // 2026-02-19 (Dave) — auto-scope-fix. The currently-selected aid
        // isn't in the list of associations this user can access
        // (common after switching accounts, or when a limited-scope
        // user visits a URL that was bookmarked while a broader
        // account was signed in). Silently swap to the preferred (or
        // first accessible) so the app stays usable instead of
        // parking on a 403 error banner.
        setCurrentAid(pickDefault().aid);
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setAssociationsLoaded(true);
    }
  }, [currentAid, user?.default_association_id]);

  useEffect(() => { loadAssociations(); }, []);

  // Cross-screen bus: FirmsScreen / AssociationScreen dispatch this after
  // any mutation that could affect the association list or the currently
  // loaded association's math. In addition to reloading the list we also
  // refetch the CURRENT association + its calc so the Associations tab
  // and the Firms drawer stay in lock-step even when the mutation
  // originates from the OTHER surface. Dave 2026-02-08.
  //
  // 2026-02-09 fix — preserve the ACTIVE scenario when re-fetching. Prior
  // implementation always fetched the baseline bundle, which silently
  // reset `variables` + `calc` to baseline after an Association-tab OB /
  // Members edit even though the user still had an active non-baseline
  // scenario loaded. The RIGHT-panel Contribution Summary appeared to
  // "not propagate" because the calc snapped back to the baseline
  // scenario's numbers instead of re-running the active scenario against
  // the new OB / Members.
  useEffect(() => {
    const handler = async (evt) => {
      // 2026-03-03 (Dave bug) — some callers (e.g. AssociationsAdminScreen
      // AddAssociationModal) pass `{ detail: { selectAid, screen } }` so we
      // can refresh the associations list FIRST and then switch to the
      // newly-created aid atomically. Without this, the naive sequence
      //   dispatchEvent(...); setCurrentAid(newAid);
      // races the TopBar guard that clears currentAid when it isn't in
      // the (still-stale) associations list, and the user lands on the
      // "No associations under this firm" empty state even though the
      // create succeeded.
      const detail = evt?.detail || {};
      const targetAid = detail.selectAid || null;
      const targetScreen = detail.screen || null;

      if (targetAid) {
        // Refresh the app-wide associations list BEFORE flipping currentAid
        // so TopBar's stale-list guard sees the new aid and doesn't clear it.
        try {
          const res = await axios.get(`${API}/associations`);
          setAssociations(Array.isArray(res.data) ? res.data : []);
        } catch { /* non-blocking */ }
        setAssociationsLoaded(true);
        setCurrentAid(targetAid);
        if (targetScreen && typeof setCurrentScreen === "function") {
          setCurrentScreen(targetScreen);
        }
        return;
      }

      loadAssociations();
      if (!currentAid) return;
      const scope = activeScenarioId;
      try {
        const bundleUrl = scope
          ? `${API}/associations/${currentAid}?scenario_id=${scope}`
          : `${API}/associations/${currentAid}`;
        const rb = await axios.get(bundleUrl);
        setAssociation(rb.data);
        setVariables(rb.data.variables);
        setOriginalVariables(rb.data.variables);
        const calcUrl = scope
          ? `${API}/associations/${currentAid}/calculate?scenario_id=${scope}`
          : `${API}/associations/${currentAid}/calculate`;
        const calcRes = await axios.get(calcUrl);
        setCalcWithStamp(calcRes.data);
        // Baseline calc is always the unscoped calc — used by the
        // "Verify Against Proof" strip and diff comparisons.
        const baseCalcRes = scope
          ? await axios.get(`${API}/associations/${currentAid}/calculate`)
          : calcRes;
        setBaselineCalc(baseCalcRes.data);
      } catch { /* non-blocking */ }
    };
    window.addEventListener("ureserve:associations-changed", handler);
    return () => window.removeEventListener("ureserve:associations-changed", handler);
  }, [loadAssociations, currentAid, activeScenarioId]);

  // Track previous currentAid so we can reset viewingPlanYearId only
  // when the association actually changes (not when viewingPlanYearId
  // itself changes, which is exactly the condition that would silently
  // reset the user's own prior-year selection).
  const prevAidRef = useRef(null);
  useEffect(() => {
    if (!currentAid) {
      // Bug fix 2026-08-04 (Dave) — when the user switches to a firm with
      // zero associations, TopBar clears currentAid. Drop all dependent
      // state so the Dashboard doesn't render the previous firm's stale
      // numbers. An "empty firm" placeholder is rendered below in place
      // of the normal screens.
      setAssociation(null);
      setVariables(null);
      setOriginalVariables(null);
      setActiveScenarioId(null);
      setCalcWithStamp(null);
      setBaselineCalc(null);
      setScenarios([]);
      prevAidRef.current = null;
      return;
    }
    setError(null);
    // Slice B (Dave 2026-02-17) — clear the prior-year view only when
    // the association ITSELF changes. Without this guard the effect
    // would clear the very selection whose change re-triggered it.
    if (prevAidRef.current && prevAidRef.current !== currentAid) {
      setViewingPlanYearId(null);
    }
    prevAidRef.current = currentAid;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    // Perf (2026-02-19) — Dashboard used to fire bundle → calc
    // sequentially plus a *duplicate* bundle for the preload and a
    // separate scenarios call. Fires all three primary calls in
    // parallel now; the preload piggy-backs on the bundle response so
    // it costs zero extra round-trips.
    //
    // Race guard (2026-02-19, Dave / code-review Step 2) — clicking
    // Association A then B in quick succession used to briefly flash
    // A's data over B because A's slower response arrived last. Each
    // effect run now captures `currentAid` at fire-time and every
    // `.then` verifies the aid still matches before touching state.
    const raceAid = currentAid;
    const stillCurrent = () => prevAidRef.current === raceAid;
    // 2026-03-01 (Dave) — Persist the last Go-To scenario across sessions.
    // Snapshot the saved id synchronously BEFORE firing any fetches.
    //
    // IMPORTANT (2026-03-02 correctness fix) — the backend's
    // `GET /associations/{aid}?scenario_id=X` and
    // `GET /calculate?scenario_id=X` only layer scenario X's
    // ELEMENT overrides on top of baseline. They still return the
    // BASELINE scenario's stored assumption variables (inflation_rate,
    // interest_rate, target_pct, adjustment_pct, adjustment_period).
    // To match `handleGoToScenario`, we must read the scenario's own
    // stored variables from the scenarios list and POST `/calculate`
    // with them. That means, when restoring a saved Go-To, we defer
    // setting `variables` / `originalVariables` / `activeScenarioId`
    // to `scenariosP.then` so the Dashboard never briefly renders
    // with the wrong (baseline) numbers.
    // Star-driven load: always open on the server's Go-To scenario.
    try { window.localStorage.removeItem(`ureserve.goto.${currentAid}`); } catch { /* non-blocking */ }
    // One request: association + baseline calc + scenarios (+ Go-To calc when it isn't the baseline).
    axios.get(`${API}/associations/${currentAid}/dashboard`)
      .then(async (res) => {
        if (!stillCurrent()) return;
        const { association: bundle, baseline_calc: baseCalc, scenarios: list, goto_calc: gotoCalc } = res.data;
        setAssociation(bundle);
        setBaselineCalc(baseCalc);
        const ensured = await ensureGoTo(list.scenarios);
        if (!stillCurrent()) return;
        setScenarios(ensured.list);
        const goto = ensured.goto;
        if (goto && !goto.is_baseline) {
          const v = {
            inflation_rate: goto.inflation_rate,
            interest_rate: goto.interest_rate,
            target_pct: goto.target_pct,
            adjustment_pct: goto.adjustment_pct,
            adjustment_period: goto.adjustment_period,
            horizon: 30,
          };
          setVariables(v);
          setOriginalVariables(v);
          setActiveScenarioId(goto.scenario_id);
          if (gotoCalc) {
            setCalcWithStamp(gotoCalc);
          } else {
            // Go-To was just auto-starred by ensureGoTo — compute it.
            try {
              const cc = await axios.post(`${API}/associations/${currentAid}/calculate`, { variables: v, scenario_id: goto.scenario_id });
              if (stillCurrent()) setCalcWithStamp(cc.data);
            } catch { /* non-blocking — next debounced recalc fills in */ }
          }
        } else {
          setVariables(bundle.variables);
          setOriginalVariables(bundle.variables);
          setActiveScenarioId(goto ? goto.scenario_id : bundle.baseline_scenario_id);
          setCalcWithStamp(baseCalc);
        }
        const kickPreload = () => preloadMethods(API, currentAid, bundle.variables);
        if (typeof window.requestIdleCallback === "function") {
          window.requestIdleCallback(kickPreload, { timeout: 400 });
        } else {
          setTimeout(kickPreload, 250);
        }
      })
      .catch((e) => { if (stillCurrent()) { setError(e.message); setScenarios([]); } });
    // Slice B — re-run this effect when the user picks a different
    // plan year to view; the axios interceptor stamps the id onto every
    // request so this bundle GET refetches for the selected year.
  }, [currentAid, viewingPlanYearId]);

  // Move the coral Go-To star server-side (single source of truth for what loads).
  const starScenario = async (scenarioId) => {
    if (!scenarioId) return;
    try {
      await axios.patch(`${API}/scenarios/${scenarioId}/go-to`, { is_go_to: true });
    } catch { /* view-only or network — local view still switches */ }
  };

  const refreshScenarios = async () => {
    if (!currentAid) return;
    try {
      const res = await axios.get(`${API}/associations/${currentAid}/scenarios`);
      setScenarios(res.data.scenarios);
    } catch (e) {
      /* non-blocking */
    }
  };

  // Refresh association bundle and re-run calc after element CRUD writes.
  // Accepts an updated `AssociationOut` (from POST/PATCH/DELETE responses)
  // so we avoid an extra GET round-trip on the happy path. Slice E2 fix
  // (2026-02-20): batch PATCH now also returns `new_scenario_id` for the
  // auto-created "Element edit …" scenario. When present, open the
  // SetGoToScenarioModal to ask whether to promote it to Go-To. If declined,
  // we stay on the previously active scenario — baseline element/line-item
  // data is intact, so no bleed.
  //
  // Slice E4c fix (2026-02-24) — ALWAYS re-fetch the bundle scoped to the
  // currently-active scenario. The batch endpoint returns the bundle scoped
  // to `new_scenario_id` when a delta produced a new scenario, or baseline
  // otherwise. Neither necessarily matches what the user is looking at —
  // and if the user clicks "Keep current Go-To" (or the modal backdrop),
  // storing the returned bundle produces a subtle desync:
  //   • association state → elements from the just-created scenario (e.g. 358=$0)
  //   • activeScenarioId  → still the previous scenario (e.g. baseline)
  //   • calc state         → still baseline's numbers
  // Cell shows $0 but header shows baseline values → user later types $20k
  // and the live-preview returns baseline values ($92,300 / $64,600), which
  // matches what the header was ALREADY showing → looks like the preview
  // "didn't climb". Root cause: association was pointing at the wrong scope.
  // 2026-02-28 (Dave, later) — Auto-Go-To on element saves.  Any batch
  // save that produces a `new_scenario_id` is now IMMEDIATELY promoted
  // to Go-To — no prompt.  The user's intent when they hit Save is
  // "analyse this new state", so making the just-created scenario the
  // active one is the fastest expression of that intent.  Users can
  // switch back to any prior scenario from the Scenario History modal.
  const applyAssociationBundle = async (bundle, newScenarioId, freshCalc) => {
    const aid = bundle.aid;
    // Fast path (element batch saves): the response already carries the bundle and a calc
    // run with the current sliders, so skip the re-fetch + re-calc and refresh the rest in the background.
    if (freshCalc && !newScenarioId && association?.plan_year_id === bundle.plan_year_id) {
      setAssociation(bundle);
      setCalcWithStamp(freshCalc);
      axios.get(`${API}/associations/${aid}/calculate`).then((r) => setBaselineCalc(r.data)).catch(() => {});
      refreshScenarios();
      return;
    }
    // If a new scenario was auto-created, treat it as Go-To immediately.
    const nextScope = newScenarioId || activeScenarioId;
    let scopedBundle = bundle;
    try {
      const rb = nextScope
        ? await axios.get(`${API}/associations/${aid}?scenario_id=${nextScope}`)
        : await axios.get(`${API}/associations/${aid}`);
      scopedBundle = rb.data;
    } catch { /* fall back to the batch-returned bundle */ }

    // 2026-10-08 (Dave) — Detect a plan-year transition. When a user
    // commissions a new plan year, reactivates a prior year, or
    // deletes a draft, the backend's active plan year shifts and
    // every scenario's `adjustment_pct` / `adjustment_budget_1` is
    // either shifted (new PY) or belongs to a now-archived PY. Two
    // consequences the slider state must respect:
    //   1. The user's current slider nudges belong to the OLD plan
    //      year and are now meaningless; hard-sync to the newly
    //      active baseline.
    //   2. `activeScenarioId` likely points to a scenario in the
    //      OLD plan year. The backend's GET /associations?scenario_id=
    //      quietly uses that scope and returns the OLD scenario's
    //      variables alongside the NEW plan year's elements — which
    //      is why the Dashboard sliders were sticking to the pre-
    //      reactivate values. Clear the scenario pin AND re-fetch
    //      unscoped so `scopedBundle.variables` is actually the
    //      newly-active baseline.
    // Element-edit callers hit this function too, but with the SAME
    // plan_year_id, so their slider + scenario pin are preserved.
    const planYearChanged =
      association?.plan_year_id &&
      scopedBundle?.plan_year_id &&
      association.plan_year_id !== scopedBundle.plan_year_id;
    let restoredScenarioId = null;
    if (planYearChanged) {
      setActiveScenarioId(null);
      try {
        const rb2 = await axios.get(`${API}/associations/${aid}`);
        scopedBundle = rb2.data;
      } catch { /* keep whichever bundle we already have */ }
      setVariables(scopedBundle.variables);
      setOriginalVariables(scopedBundle.variables);
      // 2026-10-08 (Dave) — The scenario pin lives server-side as
      // `is_go_to` on a Scenario row (per-plan-year). After a plan-
      // year transition the OLD activeScenarioId no longer belongs
      // to the newly active plan year, but the NEW plan year has
      // its own starred scenario (the clone of the old one, carried
      // over by POST /plan-years). Re-fetch the scenarios list and
      // auto-restore that star so the "Active Scenario" label and
      // its parameters reappear without requiring a full reload.
      try {
        const sres = await axios.get(`${API}/associations/${aid}/scenarios`);
        // 2026-10-09 (Dave) — If the returned-to plan year has no star,
        // star one so the Active Scenario card is never blank.
        const ensured = await ensureGoTo(sres.data.scenarios);
        setScenarios(ensured.list);
        const goto = ensured.goto;
        if (goto && goto.is_baseline) {
          setActiveScenarioId(goto.scenario_id);
          restoredScenarioId = goto.scenario_id;
        } else if (goto) {
          const gv = {
            inflation_rate: goto.inflation_rate,
            interest_rate: goto.interest_rate,
            target_pct: goto.target_pct,
            adjustment_pct: goto.adjustment_pct,
            adjustment_period: goto.adjustment_period,
            horizon: 30,
          };
          setVariables(gv);
          setOriginalVariables(gv);
          setActiveScenarioId(goto.scenario_id);
          restoredScenarioId = goto.scenario_id;
          // Re-fetch bundle scoped to the starred scenario so the
          // Elements table reflects its overrides, mirroring the
          // mount-time scenariosP restore path.
          try {
            const rb3 = await axios.get(
              `${API}/associations/${aid}?scenario_id=${goto.scenario_id}`,
            );
            scopedBundle = rb3.data;
          } catch { /* keep unscoped bundle */ }
        }
      } catch { /* non-blocking — leave on baseline */ }
    }
    const nextVariables = planYearChanged ? scopedBundle.variables : variables;
    const nextScopeAfter = planYearChanged ? restoredScenarioId : nextScope;

    setAssociation(scopedBundle);
    if (newScenarioId && !planYearChanged) {
      setActiveScenarioId(newScenarioId);
      await starScenario(newScenarioId);
    }

    // Refresh calc + baseline + scenarios list scoped to the (possibly new) Go-To.
    try {
      const res = await axios.post(`${API}/associations/${aid}/calculate`, {
        variables: nextVariables,
        scenario_id: nextScopeAfter || undefined,
      });
      setCalcWithStamp(res.data);
    } catch (e) { /* non-blocking */ }
    try {
      const bRes = await axios.get(`${API}/associations/${aid}/calculate`);
      setBaselineCalc(bRes.data);
    } catch { /* non-blocking */ }
    await refreshScenarios();

    // 2026-02-28 (Dave) — Nudge the user to rename the just-created auto
    // scenario so the History log stays legible.  Fetches the current
    // server-side name (with any collision suffix) so the banner shows
    // the exact string the History log will display.
    if (newScenarioId) {
      try {
        const r = await axios.get(`${API}/associations/${aid}/scenarios`);
        const found = r.data.scenarios.find((s) => s.scenario_id === newScenarioId);
        setRenameNudge({
          scenarioId: newScenarioId,
          currentName: found?.name || "Element edit — …",
          aid,
        });
      } catch {
        setRenameNudge({ scenarioId: newScenarioId, currentName: "Element edit — …", aid });
      }
    }
  };

  // Handlers for the SetGoToScenarioModal buttons.
  const onGotoKeep = () => setGotoPrompt(null);
  const onGotoActivate = async () => {
    const p = gotoPrompt;
    if (!p) return;
    setGotoPrompt(null);
    setActiveScenarioId(p.scenarioId);
    await starScenario(p.scenarioId);
    try {
      const rb = await axios.get(`${API}/associations/${p.aid}?scenario_id=${p.scenarioId}`);
      setAssociation(rb.data);
      setVariables(rb.data.variables);
      setOriginalVariables(rb.data.variables);
      const cc = await axios.post(`${API}/associations/${p.aid}/calculate`, {
        variables: rb.data.variables,
        scenario_id: p.scenarioId,
      });
      setCalcWithStamp(cc.data);
    } catch { /* non-blocking */ }
    await refreshScenarios();
  };

  // One request: create + make Go-To; response carries calc + list. Exact repeats → 409.
  const savingScenario = useRef(false);
  const handleSaveScenario = async () => {
    if (savingScenario.current) return;
    savingScenario.current = true;
    try {
      const res = await axios.post(`${API}/associations/${currentAid}/scenarios`, {
        variables, make_go_to: true,
      });
      const { scenario_id: newId, calc: freshCalc, scenarios: list } = res.data;
      setOriginalVariables(variables); // saved → current is now the baseline for undo
      setActiveScenarioId(newId);
      if (list) setScenarios(list);
      if (freshCalc) setCalcWithStamp(freshCalc);
      setAssociation((a) => (a ? { ...a, variables: { ...variables }, scenario_name: res.data.identifier } : a));
      toast.success(`Scenario saved: ${res.data.name || res.data.identifier}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message || "Save failed");
    } finally {
      savingScenario.current = false;
    }
  };

  const handleGoToScenario = async (s, { keepOpen = false, toDashboard = false } = {}) => {
    const v = {
      inflation_rate: s.inflation_rate,
      interest_rate: s.interest_rate,
      target_pct: s.target_pct,
      adjustment_pct: s.adjustment_pct,
      adjustment_period: s.adjustment_period,
      horizon: 30,
    };
    // Set state so sliders reflect the new scenario immediately.
    setVariables(v);
    setOriginalVariables(v);
    setActiveScenarioId(s.scenario_id);
    if (!keepOpen) setShowHistoryModal(false);
    if (toDashboard) {
      setCurrentScreen("Dashboard");
      toast.success(`Now viewing: ${String(s.name || "").trim() || s.identifier}`, { duration: 3500 });
    }

    // 2026-02-28 (Dave) — Refresh the scenarios list so the coral
    // star we just moved via the modal (or via a "Set as Go-To"
    // action elsewhere) is reflected in App.js state.  Without this
    // the next open of the History modal shows the stale star until
    // scenarios re-fetch on some other trigger.
    refreshScenarios();

    // Also reload the bundle scoped to this scenario so the Elements table
    // reflects its overrides (if any). Baseline scenarios have no overrides
    // so this is a no-op for them, but it's still worth re-fetching to keep
    // state consistent.
    if (currentAid) {
      try {
        const rb = await axios.get(
          `${API}/associations/${currentAid}?scenario_id=${s.scenario_id}`,
        );
        setAssociation(rb.data);
      } catch { /* non-blocking */ }
    }

    // The debounced recalculate useEffect skips when variables === originalVariables
    // (undo behaviour). Because we just set both to the SAME object here, that
    // guard fires and no recalc happens. Fetch imperatively — same pattern as
    // onUndo — so the Dashboard reflects the switched scenario's numbers.
    if (!currentAid) return;
    const seq = ++requestSeq.current;
    setRunning(true);
    setError(null);
    try {
      const res = await axios.post(`${API}/associations/${currentAid}/calculate`, {
        variables: v,
        scenario_id: s.scenario_id,
      });
      if (seq === requestSeq.current) setCalcWithStamp(res.data);
    } catch (e) {
      if (seq === requestSeq.current) setError(e.message);
    } finally {
      if (seq === requestSeq.current) setRunning(false);
    }
  };

  const handleScenarioDeleted = async (id, response) => {
    setScenarios((prev) => prev.filter((s) => s.scenario_id !== id));
    const wasActive = id === activeScenarioId;
    // 2026-03-01 (Dave) — When the deleted row was the baseline, the
    // backend auto-promotes another scenario (response.promoted_replacement).
    // In that case the plan-year line items just mutated and a different
    // scenario now holds is_baseline — force the full bundle reload so the
    // UI reflects the new baseline everywhere.
    const wasBaselinePromoted = !!response?.promoted_replacement;
    if ((wasActive || wasBaselinePromoted) && association) {
      const aid = association.aid;
      try {
        const rb = await axios.get(`${API}/associations/${aid}`);
        setAssociation(rb.data);
        setVariables(rb.data.variables);
        setOriginalVariables(rb.data.variables);
        setActiveScenarioId(rb.data.baseline_scenario_id);
        if (wasActive) await starScenario(rb.data.baseline_scenario_id);
        const cc = await axios.get(`${API}/associations/${aid}/calculate`);
        setCalcWithStamp(cc.data);
        setBaselineCalc(cc.data);
      } catch { /* non-blocking */ }
    }
    // Re-fetch the authoritative scenarios list so year1_budget/SA columns
    // reflect any downstream effects and no stale row lingers.
    await refreshScenarios();
  };

  // 2026-02-28 (Dave) — After a scenario is promoted to baseline, force a
  // full reload of the association bundle + scenarios list. The baseline
  // line items just mutated permanently, the promoted scenario is now
  // baseline, and the previous baseline is either gone or demoted. Simplest
  // way to reflect all of that consistently is a from-scratch refetch.
  const handleScenarioPromoted = async () => {
    if (!association) return;
    const aid = association.aid;
    try {
      const rb = await axios.get(`${API}/associations/${aid}`);
      setAssociation(rb.data);
      setVariables(rb.data.variables);
      setOriginalVariables(rb.data.variables);
      setActiveScenarioId(rb.data.baseline_scenario_id);
      await starScenario(rb.data.baseline_scenario_id);
      const cc = await axios.get(`${API}/associations/${aid}/calculate`);
      setCalcWithStamp(cc.data);
      setBaselineCalc(cc.data);
    } catch { /* non-blocking */ }
    await refreshScenarios();
  };


  const handleScenarioRenamed = (id, updates) => {
    // 2026-02-27 (evening, Dave) — Now also handles Anchor-row
    // variable edits (target_pct / inflation_rate / interest_rate /
    // adjustment_pct / adjustment_period) coming back from the
    // Scenarios modal. Merge every returned field into the local
    // scenarios array, and — when the changed scenario is the
    // active/Go-To — push its numeric variables into `variables`
    // state AND kick an imperative /calculate so every chart on
    // the Dashboard / Association screen repaints with the new
    // numbers (mirrors handleGoToScenario's imperative recalc; the
    // debounced useEffect would skip when variables ===
    // originalVariables).
    setScenarios((prev) =>
      prev.map((s) => (s.scenario_id !== id ? s : { ...s, ...updates }))
    );
    if (id !== activeScenarioId) return;
    const varKeys = [
      "target_pct",
      "inflation_rate",
      "interest_rate",
      "adjustment_pct",
      "adjustment_period",
    ];
    const varUpdates = {};
    for (const k of varKeys) {
      if (k in updates) varUpdates[k] = updates[k];
    }
    if (Object.keys(varUpdates).length === 0) return;
    setVariables((prev) => {
      const next = prev ? { ...prev, ...varUpdates } : prev;
      setOriginalVariables(next);
      // Kick an imperative /calculate so the app-wide charts
      // repaint in place. Non-blocking; guarded by requestSeq.
      if (currentAid && next) {
        const seq = ++requestSeq.current;
        axios
          .post(`${API}/associations/${currentAid}/calculate`, {
            variables: next,
            scenario_id: id,
          })
          .then((res) => {
            if (seq === requestSeq.current) setCalcWithStamp(res.data);
          })
          .catch(() => { /* non-blocking */ });
      }
      return next;
    });
  };

  // Inline "[Unnamed Scenario]" naming (Dashboard + Association tab) — same rules as Scenario History rename.
  const nameActiveScenario = (() => {
    const s = scenarios.find((x) => x.scenario_id === activeScenarioId);
    if (!s || !user || isViewOnly(user)) return undefined;
    const mine = s.created_by_user_id && String(s.created_by_user_id) === String(user.user_id);
    if (!isAdmin(user) && !mine) return undefined;
    return async (name) => {
      const res = await axios.patch(`${API}/scenarios/${s.scenario_id}`, { name });
      handleScenarioRenamed(s.scenario_id, res.data);
    };
  })();


  useEffect(() => {
    if (!currentAid || !variables || !originalVariables) return;
    if (variables === originalVariables) return;

    if (debounceRef.current) clearTimeout(debounceRef.current);
    // Engine runs server-side only (IP protection, Dave 2026-10-09).
    debounceRef.current = setTimeout(async () => {
      const seq = ++requestSeq.current;
      setRunning(true);
      setError(null);
      try {
        const res = await axios.post(`${API}/associations/${currentAid}/calculate`, {
          variables,
          scenario_id: activeScenarioId || undefined,
        });
        if (seq === requestSeq.current) setCalcWithStamp(res.data);
      } catch (e) {
        if (seq === requestSeq.current) setError(e.message);
      } finally {
        if (seq === requestSeq.current) setRunning(false);
      }
    }, 120);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [variables, currentAid, originalVariables, activeScenarioId]);

  const onVariableChange = (key, value) => {
    setVariables((prev) => {
      if (key === "adjustment_period" && (prev.adjustment_pct ?? 0) === 0) {
        return { ...prev, adjustment_period: 0 };
      }
      const next = { ...prev, [key]: value };
      // Couple Y1 Adjustment % ↔ Adjustment Period: when the adjustment
      // is zero the period has no meaning, so force it to 0. When an
      // adjustment is (re)introduced from 0, bump the period back to 1
      // so the stepper starts at its new minimum.
      if (key === "adjustment_pct") {
        if (value === 0) {
          next.adjustment_period = 0;
        } else if ((prev.adjustment_period ?? 0) === 0) {
          next.adjustment_period = 1;
        }
      }
      return next;
    });
    setRunning(true); // reflect intent immediately; cleared when request resolves
  };

  const onUndo = async () => {
    if (!originalVariables || !currentAid) return;
    setVariables(originalVariables);
    setRunning(true);
    setError(null);
    try {
      const url = activeScenarioId
        ? `${API}/associations/${currentAid}/calculate?scenario_id=${activeScenarioId}`
        : `${API}/associations/${currentAid}/calculate`;
      const res = await axios.get(url);
      setCalcWithStamp(res.data);
    } catch (e) {
      setError(e.message);
    } finally {
      setRunning(false);
    }
  };

  const isDirty = !!(
    variables &&
    originalVariables &&
    Object.keys(originalVariables).some(
      (k) => Math.abs((variables[k] ?? 0) - (originalVariables[k] ?? 0)) > 1e-9,
    )
  );

  // 2026-02-23 (Dave) — First-login boot fade. Hides the entire app
  // (chrome + content) behind a centered spinner until the initial
  // Dashboard data is loaded, then fades the whole app in together in
  // one clean beat. Runs ONCE per session — subsequent tab switches
  // (Dashboard / Association / Elements / etc.) never re-hide the
  // chrome. The Dashboard's own inner fade still runs on later
  // re-mounts (Option A behavior preserved).
  const [firstBootFaded, setFirstBootFaded] = useState(
    () =>
      typeof window !== "undefined" &&
      window.sessionStorage?.getItem("ureserve.booted") === "true",
  );
  const bootReady = !!(calc && association && variables);
  useEffect(() => {
    if (bootReady && !firstBootFaded) {
      // Match the Dashboard's 480 ms settle window so Recharts finishes
      // its bar / donut animations behind the veil, then fade in.
      const t = setTimeout(() => {
        setFirstBootFaded(true);
        try {
          window.sessionStorage?.setItem("ureserve.booted", "true");
        } catch (_) {
          // sessionStorage may be blocked (Safari private mode); the
          // in-memory state still carries us through the current tab.
        }
      }, 480);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [bootReady, firstBootFaded]);

  return (
    <>
      {!firstBootFaded && !bootReady && (
        <div
          className="fixed inset-0 flex items-center justify-center z-[70]"
          style={{ background: "#FAFAF9" }}
          data-testid="app-boot-spinner"
        >
          <div
            className="flex items-center tracking-wide"
            style={{
              fontSize: 30,
              fontWeight: 700,
              transform: "translateY(-64px)",
              // 2026-03-01 (Dave) — same +25% spinner + 16 px gap as
              // the pre-hydration and pre-auth spinners so all three
              // are visually identical across every mount handoff.
              gap: 16,
              // 2026-02-27 (Dave) — lock font to the same system stack
              // the pre-hydration spinner in index.html uses, so React's
              // mount doesn't visibly swap from `system-ui` to
              // `Source Sans 3` when the webfont finishes downloading.
              // Chrome exposed that swap as a "loading text pops
              // larger then shrinks" glitch on cold load; Safari
              // masked it. Boot spinner is transient — no branding
              // lost by using the platform font here.
              fontFamily:
                'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
            }}
          >
            <span
              className="inline-block rounded-full animate-spin"
              style={{
                width: 35,
                height: 35,
                border: "6px solid #0091D5",
                borderTopColor: "#EA6A47",
              }}
              aria-hidden="true"
            />
            <span>
              <span style={{ color: "#A8B3BF", fontSize: 26 }}>loading </span>
              <span style={{ color: "#1C4E80" }}>u<em>Reserve</em> Planning</span>
            </span>
          </div>
        </div>
      )}
    <div
      className="app-shell flex flex-col"
      style={{
        background: "#FAFAF9",
        opacity: firstBootFaded ? 1 : 0,
        transition: "opacity 220ms ease-out",
      }}
    >
      <MaintenanceBanner platformState={platformState} />
      <TopBar
        associations={associations}
        associationsLoaded={associationsLoaded}
        currentAid={currentAid}
        onSelect={(aid) => {
          // Picking a different association always lands on its Dashboard.
          if (aid !== currentAid) setCurrentScreen("Dashboard");
          setCurrentAid(aid);
        }}
        devMode={devMode}
        onExitDevMode={disableDevMode}
        horizon={displayHorizon}
        onHorizonChange={setDisplayHorizon}
        currentScreen={currentScreen}
        onScreenChange={setCurrentScreen}
        viewingPlanYearId={viewingPlanYearId}
        onPickPlanYear={setViewingPlanYearId}
        currentTier={association?.tier || "pro"}
        currentPlanYear={association?.aid === currentAid ? association?.study_year : null}
        elementCount={association?.elements?.length}
      />

      {/* Slice B (Dave 2026-02-17) — Prior-year viewing banner. Renders
          only when the user has explicitly picked a closed plan year
          from the TopBar picker. Coral strip so it's impossible to
          misread the Dashboard as the current year, with an inline
          "Return to Active" action. */}
      {viewingPlanYearId && association && (
        <div
          className="flex items-center justify-between px-4 py-1.5 text-[11.5px] font-semibold"
          style={{
            background: "#FCEDEA",
            color: "#9A2E1A",
            borderBottom: "2px solid #EA6A47",
          }}
          data-testid="viewing-prior-year-banner"
        >
          <span className="flex items-center gap-2">
            <Info size={12} className="text-coral" />
            You are viewing a closed plan year (
            <b className="font-mono">{association.study_year}</b>).
            All data on this page is read-only.
          </span>
          <button
            type="button"
            onClick={() => setViewingPlanYearId(null)}
            data-testid="viewing-prior-year-return"
            className="inline-flex items-center gap-1 px-2 py-0.5 border border-coral rounded-[3px] text-coral hover:bg-coral hover:text-white transition-colors text-[11px]"
          >
            Return to Active Year
          </button>
        </div>
      )}

      <main
        className={
          "w-full mx-auto flex flex-col flex-1" +
          (viewingPlanYearId ? " readonly-view" : "")
        }
        data-readonly-view={viewingPlanYearId ? "true" : undefined}
        style={{
          maxWidth: "var(--u-content-max)",
          // Dashboard edges are Platform Settings (side 28 = automatic gutter).
          padding: currentScreen === "Dashboard"
            ? `${Number(platformSettings.dashboard_edge_tb_px ?? 6)}px ${Number(platformSettings.dashboard_edge_side_px ?? 28) === 28 ? "var(--u-gutter)" : `${Number(platformSettings.dashboard_edge_side_px)}px`}`
            : "6px var(--u-gutter)",
          gap: "var(--u-panel-gap)",
          minHeight: 0,
        }}
      >
        {error && (
          <div
            className="bg-coral-wash border border-coral px-3 py-1 text-[12px] text-ink"
            data-testid="error-banner"
          >
            <Info size={12} className="inline mr-1.5 text-coral" />
            {error}
          </div>
        )}

        {/* Slice 5c (2026-02-24) — ControlsStrip is association-scoped
            (association name, scenario history, verify-proof). Hide it on
            admin/reference screens where those actions don't apply.
            2026-08-04 (Dave bug) — also hide it when no association is
            loaded, so the strip doesn't render blank data.
            2026-02-28 (Dave) — Contributions dropped from the whitelist;
            the tab renders its own compact navy header that hosts
            Scenario History inline, so the outer ControlsStrip would
            just be a redundant "Demo Association A" band on that screen. */}
        {association && currentScreen === "Association" && (
          <ControlsStrip
            association={association}
            variables={variables}
            onOpenAssociationAttachments={() => setShowAssocAttachments(true)}
            onOpenReport={() => setShowReportModal(true)}
            onPlanYearChanged={applyAssociationBundle}
            associationAttachmentCount={assocAttachmentCount}
            running={running}
            currentScreen={currentScreen}
          />
        )}

        {/* 2026-08-04 (Dave bug) — Association-scoped screens render an
            empty-state placeholder when the current firm has no
            associations. Show it only when (a) initial fetch has finished
            AND (b) there is no currentAid at all. That way the placeholder
            fires ONLY when TopBar cleared currentAid because the firm was
            truly empty — never during the transient "aid set but bundle
            not yet fetched" window that briefly happens on login. */}
        {associationsLoaded && !currentAid && ["Dashboard", "Association", "Elements", "Projections", "Contributions", "Illustrations"].includes(currentScreen) && (
          <div className="flex-1 flex items-center justify-center" data-testid="empty-firm-state">
            <div className="panel-compact max-w-md text-center" style={{ padding: "32px 40px" }}>
              <div className="text-[13px] font-bold text-navy mb-1">No associations under this firm</div>
              <div className="text-[11.5px] text-slate mb-3">
                Add an association via the <span className="font-semibold text-navy">Firms</span> tab
                (drawer → New Association).
              </div>
              <button
                onClick={() => setCurrentScreen("Firms")}
                data-testid="empty-firm-goto-firms"
                className="px-3 py-1.5 bg-navy text-white text-[11px] font-semibold rounded-[3px]"
              >
                Open Firms tab
              </button>
            </div>
          </div>
        )}

        {association && currentScreen === "Dashboard" && (
          <DashboardScreen
            calc={calc}
            association={association}
            variables={variables}
            originalVariables={originalVariables}
            onVariableChange={onVariableChange}
            onUndo={onUndo}
            onSave={handleSaveScenario}
            onOpenHistory={() => setShowHistoryModal(true)}
            onPlanYearChanged={applyAssociationBundle}
            scenarioCount={scenarios.length}
            activeScenarioName={
              scenarioLabel(scenarios.find((s) => s.scenario_id === activeScenarioId))
            }
            activeScenarioTitle={(() => {
              const s = scenarios.find((x) => x.scenario_id === activeScenarioId);
              return s ? s.name || "" : undefined; // "" = unnamed (shows placeholder)
            })()}
            onNameActive={nameActiveScenario}
            horizon={displayHorizon}
          />
        )}
        {association && currentScreen === "Association" && (
          <AssociationScreen
            association={association}
            calc={calc}
            variables={variables}
            onSaved={applyAssociationBundle}
            activeScenarioName={(() => {
              const s = scenarios.find((x) => x.scenario_id === activeScenarioId);
              return s ? s.name || "" : undefined; // "" = unnamed (shows placeholder)
            })()}
            onNameActive={nameActiveScenario}
          />
        )}
        {association && currentScreen === "Elements" && (
          <ElementsScreen
            association={association}
            onBundleUpdated={applyAssociationBundle}
            currentVariables={variables}
            activeScenarioId={activeScenarioId}
            calc={calc}
            onPreviewLoadingChange={setElementsRecalcActive}
          />
        )}
        {association && currentScreen === "Projections" && (
          <ProjectionsScreen calc={calc} variables={variables} horizon={displayHorizon} calcTimestamp={calcTimestamp} />
        )}
        {association && currentScreen === "Contributions" && (
          <ContributionsScreen
            association={association}
            variables={variables}
            ffbByYear={calc?.ffb_peak_by_year || null}
            horizon={displayHorizon}
            chartView={contribChartView}
            onDismissChart={() => setContribChartView(false)}
            onToggleChart={() => setContribChartView((v) => !v)}
          />
        )}
        {association && currentScreen === "Illustrations" && (
          <IllustrationsScreen calc={calc} association={association} variables={variables} horizon={displayHorizon} />
        )}
        {currentScreen === "Support" && <SupportScreen />}
        {currentScreen === "Admin" && <AdminScreen onScreenChange={setCurrentScreen} />}
        {currentScreen === "Firms" && <FirmsScreen onScreenChange={setCurrentScreen} />}
        {currentScreen === "Users" && <UsersScreen />}
        {currentScreen === "Audit" && <AuditTrailScreen />}
        {currentScreen === "EmailLog" && <EmailLogScreen />}
        {currentScreen === "Subscriptions" && <SubscriptionsScreen />}
        {currentScreen === "Associations" && (
          <AssociationsAdminScreen
            onSelectAid={setCurrentAid}
            onScreenChange={setCurrentScreen}
          />
        )}
        {currentScreen === "Reports" && <ReportsScreen />}
        {currentScreen === "Development" && <DevelopmentScreen />}
        {currentScreen === "Resources" && <ResourcesScreen />}
      </main>

      {showCalendar && <CalendarModal calc={calc} onClose={() => setShowCalendar(false)} />}
      {showHistoryModal && (
        <ScenarioHistoryModal
          associationName={association?.name}
          scenarios={scenarios}
          activeScenarioId={activeScenarioId}
          onGoTo={handleGoToScenario}
          onDeleted={handleScenarioDeleted}
          onRenamed={handleScenarioRenamed}
          onClose={() => setShowHistoryModal(false)}
        />
      )}
      {showAssocAttachments && association && (
        <AttachmentsModal
          association={{ aid: association.aid, name: association.name }}
          canWrite={true}
          onClose={() => setShowAssocAttachments(false)}
          onChanged={() => {
            // 2026-03-01 (Dave) — Bust the hover-preview cache so the
            // pill's popover reflects the just-added/deleted file.
            invalidateAttachmentHoverCache("association", association.aid);
            refreshAssocAttachmentCount();
          }}
        />
      )}
      {/* 2026-03-04 (Dave · Option A) — In-place Reserve Budget Report
          overlay. Renders full-screen on top of the app when the
          ControlsStrip button was clicked. Replaces the old new-tab
          flow (still available via "Open in New Tab" inside the
          modal banner for anyone who wants a persistent tab). */}
      {showReportModal && currentAid && (
        <ReportModal
          aid={currentAid}
          onClose={() => setShowReportModal(false)}
        />
      )}
      {gotoPrompt && (
        <SetGoToScenarioModal
          scenarioName={gotoPrompt.scenarioName}
          onKeep={onGotoKeep}
          onActivate={onGotoActivate}
        />
      )}
      {/* 2026-02-28 (Dave) — Post-save inline rename nudge. Slim navy
          banner at top-right that auto-appears after a batch save
          creates an auto-scenario. User can rename inline (Enter) or
          dismiss (Escape / ✕). Auto-dismisses after 12 s so it doesn't
          linger. */}
      {renameNudge && (
        <RenameNudgeBanner
          key={renameNudge.scenarioId}
          scenarioId={renameNudge.scenarioId}
          currentName={renameNudge.currentName}
          aid={renameNudge.aid}
          onDone={() => setRenameNudge(null)}
          onRenamed={handleScenarioRenamed}
        />
      )}
    </div>
    </>
  );
}

// Display label: immutable ID, then the optional user Name.
const scenarioLabel = (s) => (s ? [s.identifier || s.name, s.identifier && s.name].filter(Boolean).join(" — ") : undefined);

// ─────────────────────────────────────────────────────────────────────
// RenameNudgeBanner (2026-02-28, Dave)
// Fixed-position, non-blocking banner that lets the user rename the
// auto-created scenario inline right after Save.  Enter commits;
// Escape dismisses.  Auto-dismisses in 12 s so it never lingers.
// ─────────────────────────────────────────────────────────────────────
function RenameNudgeBanner({ scenarioId, currentName, aid, onDone, onRenamed }) {
  const [value, setValue] = React.useState(currentName);
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState(null);
  const inputRef = React.useRef(null);

  React.useEffect(() => {
    const t = setTimeout(onDone, 12000);
    setTimeout(() => inputRef.current?.select(), 60);
    return () => clearTimeout(t);
  }, [onDone]);

  const commit = async () => {
    const trimmed = value.trim();
    if (!trimmed || trimmed === currentName) { onDone(); return; }
    setBusy(true); setError(null);
    try {
      await axios.patch(`${API}/scenarios/${scenarioId}`, { name: trimmed });
      if (typeof onRenamed === "function") onRenamed(scenarioId, { name: trimmed });
      onDone();
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
      setBusy(false);
    }
  };
  const onKey = (e) => {
    if (e.key === "Enter") { e.preventDefault(); commit(); }
    if (e.key === "Escape") { e.preventDefault(); onDone(); }
  };

  return (
    <div
      style={{
        position: "fixed", top: 12, right: 12, zIndex: 60,
        background: "#0F2A44", color: "#fff",
        borderLeft: "4px solid #EA6A47",
        borderRadius: 4, padding: "10px 12px",
        boxShadow: "0 8px 24px rgba(15,42,68,0.28)",
        fontSize: 12, minWidth: 340, maxWidth: 420,
      }}
      data-testid="rename-nudge"
    >
      <div style={{ marginBottom: 6, fontWeight: 600, letterSpacing: ".02em" }}>
        New scenario saved · rename for the History log?
      </div>
      <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
        <input
          ref={inputRef}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={onKey}
          disabled={busy}
          maxLength={80}
          style={{
            flex: 1, background: "#fff", color: "#0F2A44",
            border: "1px solid #EA6A47", borderRadius: 3,
            padding: "5px 8px", fontSize: 12, outline: "none",
          }}
          data-testid="rename-nudge-input"
        />
        <button
          onClick={commit}
          disabled={busy}
          style={{
            background: "#EA6A47", color: "#fff", border: 0,
            borderRadius: 3, padding: "5px 10px", fontSize: 11,
            fontWeight: 700, letterSpacing: ".04em", cursor: "pointer",
          }}
          data-testid="rename-nudge-save"
        >{busy ? "…" : "Rename"}</button>
        <button
          onClick={onDone}
          disabled={busy}
          style={{
            background: "transparent", color: "#c9d3e0",
            border: "1px solid #2e4560", borderRadius: 3,
            padding: "5px 8px", fontSize: 11, cursor: "pointer",
          }}
          data-testid="rename-nudge-dismiss"
        >Dismiss</button>
      </div>
      {error && (
        <div style={{ marginTop: 6, color: "#F7B0A0", fontSize: 11 }}>
          {error}
        </div>
      )}
    </div>
  );
}

// Starred (Go-To) scenario for the active plan year. If none is starred,
// star the first scenario server-side so an Active Scenario always shows.
async function ensureGoTo(scenarios) {
  const list = scenarios || [];
  const starred = list.find((s) => s.is_go_to);
  if (starred || !list.length) return { goto: starred || null, list };
  const pick = list[0];
  try {
    await axios.patch(`${API}/scenarios/${pick.scenario_id}/go-to`, { is_go_to: true });
    return {
      goto: { ...pick, is_go_to: true },
      list: list.map((s) => ({ ...s, is_go_to: s.scenario_id === pick.scenario_id })),
    };
  } catch {
    return { goto: pick, list };
  }
}

export default function App() {
  return (
    <AuthProvider>
      <AppShell />
    </AuthProvider>
  );
}

// Small wrapper that reads the auth state and drives whether the
// SettingsProvider should be fetching from /platform-settings/public.
// Public settings are only meaningful for authenticated users; before
// login we hold defaults so the LoginScreen never fires the fetch.
function AppShell() {
  const { user } = useAuth();
  const isAuthed = !!user && user !== null;
  return (
    <SettingsProvider isAuthenticated={isAuthed}>
      <AppGate />
    </SettingsProvider>
  );
}

// Slice 3: the app is now GATED. Non-authenticated users see the login
// screen — nothing else. Once signed in they get the full Shell.
function AppGate() {
  const { user, acknowledgeSop, changePassword, logout, refresh, tokenExp, expireSession } = useAuth();

  // 2026-02-27 (Dave) — Remove the pre-hydration boot spinner
  // (#u-preboot, added inline in index.html) as soon as React knows
  // what to render — i.e. the /auth/me call has completed one way or
  // the other. Same DOM element from first paint to fade-out, so
  // Chrome never has a chance to render two adjacent boot spinners
  // and show a font-swap / color / letter-spacing pop.
  React.useEffect(() => {
    if (user === null) return; // still hydrating
    // 2026-03-04 (Dave) — Reserve Budget Report routes own their own
    // preboot lifecycle. Skip removal here so the "Generating Reserve
    // Budget Report" spinner stays up until the ReserveBudgetReport
    // component has fetched all three endpoints AND run its `derived`
    // useMemo — otherwise the user sees the spinner disappear, a
    // plain "Loading Reserve Budget Report…" text flash in the top
    // corner, and then the report content pop in.
    const raw = (typeof window !== "undefined" ? window.location.hash : "") || "";
    if (raw.startsWith("#report/reserve-budget/")) return;
    const el = document.getElementById("u-preboot");
    if (!el) return;
    el.classList.add("u-preboot--fading");
    const done = () => el.remove();
    el.addEventListener("transitionend", done, { once: true });
    // Safety net in case transitionend never fires (e.g. reduced-motion).
    setTimeout(done, 350);
  }, [user]);

  const [ackError, setAckError] = React.useState("");
  const [pwError, setPwError] = React.useState("");
  // 2026-02-15 (Dave) — Manual change-password flow. Triggered by the
  // `ureserve:open-change-password` CustomEvent (dispatched from the
  // User menu). Independent state from the forced-flow (which is
  // driven by `user.must_change_password === true`) so the two paths
  // never collide.
  const [manualCPOpen, setManualCPOpen] = React.useState(false);
  const [manualCPToast, setManualCPToast] = React.useState("");
  // 2026-02-19 (Dave) — self-service Forgot password? state.
  //   * forgotOpen holds either false or { email: "..."} when the modal
  //     is up. LoginScreen dispatches `ureserve:open-forgot-password`
  //     with the currently-typed email so the modal is pre-filled.
  //   * resetTokenActive is a boolean derived from the URL hash; when
  //     the hash is `#reset?token=...` the reset modal renders on top of
  //     whatever the current gate would show. Works whether the caller
  //     is signed in or not — the AuthContext logout preservation added
  //     for the invite flow also keeps `#reset?token=` intact.
  const [forgotOpen, setForgotOpen] = React.useState(false);
  const [resetTokenActive, setResetTokenActive] = React.useState(() => {
    if (typeof window === "undefined") return false;
    return (window.location.hash || "").startsWith("#reset");
  });

  React.useEffect(() => {
    const onOpen = () => setManualCPOpen(true);
    window.addEventListener("ureserve:open-change-password", onOpen);
    return () => window.removeEventListener("ureserve:open-change-password", onOpen);
  }, []);

  // 2026-02-19 (Dave) — self-service name edit (dispatched from user pill
  // → Edit name). Uses AuthContext.refresh() on save so the new name
  // propagates across the TopBar, Users list, and every visible mention.
  const [editNameOpen, setEditNameOpen] = React.useState(false);
  React.useEffect(() => {
    const onOpen = () => setEditNameOpen(true);
    window.addEventListener("ureserve:open-edit-name", onOpen);
    return () => window.removeEventListener("ureserve:open-edit-name", onOpen);
  }, []);

  // 2026-02-19 (Dave) — wire the Forgot password? link on the sign-in
  // screen to open the modal here (so it can render as a top-level
  // portal, above LoginScreen). Detail carries the currently-typed
  // email as a prefill hint.
  React.useEffect(() => {
    const onOpen = (e) => setForgotOpen({ email: e?.detail?.email || "" });
    window.addEventListener("ureserve:open-forgot-password", onOpen);
    return () => window.removeEventListener("ureserve:open-forgot-password", onOpen);
  }, []);

  // Keep resetTokenActive in sync with the hash (invite links + browser
  // history / manual URL edits all fire `hashchange`).
  React.useEffect(() => {
    const sync = () => setResetTokenActive((window.location.hash || "").startsWith("#reset"));
    window.addEventListener("hashchange", sync);
    return () => window.removeEventListener("hashchange", sync);
  }, []);

  // 2026-02-28 (Dave) — Landing ↔ LoginScreen re-render tick.  The
  // logged-out branch below returns LandingScreen or LoginScreen
  // based on `window.location.hash`, but React can't detect direct
  // hash mutation.  This tick state bumps on every `hashchange` so
  // the branch is re-evaluated when Landing's "Sign In" link (or the
  // login screen's "Back to marketing" link) mutates the hash.
  const [, setLandingHashTick] = React.useState(0);
  React.useEffect(() => {
    const bump = () => setLandingHashTick((n) => n + 1);
    window.addEventListener("hashchange", bump);
    return () => window.removeEventListener("hashchange", bump);
  }, []);

  // 2026-02-28 (Dave) — Public feature toggle: does the site show
  // the marketing landing to anonymous visitors, or drop straight to
  // LoginScreen?  Sourced from platform_settings.web_enrollment_enabled
  // (1 → landing on, 0 → landing hidden).  Fetched once from the
  // public settings endpoint; while it's loading we default to
  // `undefined` and render nothing so we never flash the wrong
  // screen for a half-second on cold load.  If the fetch fails we
  // fall back to `true` — landing is the safer default because it
  // preserves the sign-in link.
  const [webEnrollEnabled, setWebEnrollEnabled] = React.useState(undefined);
  React.useEffect(() => {
    let cancelled = false;
    fetch(`${API}/platform-settings/anonymous`)
      .then((r) => r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`)))
      .then((data) => {
        if (cancelled) return;
        // Endpoint returns `{ key: value, ... }` with ints for toggles.
        const raw = data && data.web_enrollment_enabled;
        setWebEnrollEnabled(raw === 0 || raw === "0" ? false : true);
      })
      .catch(() => { if (!cancelled) setWebEnrollEnabled(true); });
    return () => { cancelled = true; };
  }, []);

  // 2026-02-19 (Dave) — Invite-link stale-session guard.
  //
  // When an admin invites a new user, the email link points to
  // `.../#login?invite=<email>`. If the recipient's browser is ALREADY
  // signed in as a different account (very common when the person
  // sending the invite tests it in the same browser they're logged in
  // on) `AppGate` would happily render THAT account's Dashboard and the
  // recipient would never see the invited account's login form — let
  // alone the forced-password-change modal.
  //
  // BULLETPROOF PATTERN (2026-02-19 revision):
  //   1. Capture the invite email **synchronously on first render** into
  //      a ref. This runs BEFORE any effect (Shell's hash normalizer,
  //      React Router, anything else) can mutate the hash. `useRef` +
  //      the null-guard means we only read once, ever.
  //   2. If we captured a value AND `user` becomes truthy at any point,
  //      restore the invite hash (in case it got stripped) and call
  //      `logout()`. The ref is cleared on use to guarantee one-shot
  //      semantics — a subsequent legit login can't accidentally re-fire.
  //   3. Also listen for `hashchange` (browser back/forward, manual URL
  //      edits) so a link click that triggers a new hash without a full
  //      reload still kicks in.
  const inviteEmailAtMountRef = React.useRef(null);
  if (inviteEmailAtMountRef.current === null) {
    try {
      const h = window.location.hash || "";
      const qi = h.indexOf("?");
      if (qi >= 0 && h.startsWith("#login")) {
        const params = new URLSearchParams(h.slice(qi + 1));
        inviteEmailAtMountRef.current = params.get("invite") || "";
      } else {
        inviteEmailAtMountRef.current = "";
      }
    } catch {
      inviteEmailAtMountRef.current = "";
    }
  }

  React.useEffect(() => {
    // 2026-02-19 (Dave) v3 — read the CURRENT hash on every effect
    // pass, not just the mount snapshot. Covers two race modes that v2
    // missed:
    //   * User was on Dashboard, then clicked an invite link that
    //     opened in a NEW TAB (previous browser tab had a stale JS
    //     bundle) — v2's mount ref captured "" in that tab; v3 reads
    //     the live hash and detects the invite regardless.
    //   * SPA in-tab hash swap — v2 fell through to the hashchange
    //     listener, which is fine, but this consolidates the two code
    //     paths into a single check that runs whenever `user` changes.
    const readInvite = () => {
      try {
        const h = window.location.hash || "";
        const qi = h.indexOf("?");
        if (qi < 0 || !h.startsWith("#login")) return "";
        return new URLSearchParams(h.slice(qi + 1)).get("invite") || "";
      } catch { return ""; }
    };
    const enforce = () => {
      const invited = readInvite();
      if (!invited || !user) return;
      if ((user.email || "").toLowerCase() === invited.toLowerCase()) {
        // Signed-in user IS the invited recipient — nothing to do.
        // Clear the mount-time capture defensively so no downstream
        // logic re-considers this after they've completed the flow.
        inviteEmailAtMountRef.current = "";
        return;
      }
      // Stale session belongs to someone else — restore the invite
      // hash (in case Shell's normalizer stripped it) and kick.
      try {
        const desired = `#login?invite=${encodeURIComponent(invited)}`;
        if (window.location.hash !== desired) {
          window.history.replaceState(
            null,
            "",
            `${window.location.pathname}${window.location.search}${desired}`
          );
        }
      } catch { /* ignore */ }
      inviteEmailAtMountRef.current = "";
      logout();
    };
    enforce();
    window.addEventListener("hashchange", enforce);
    return () => window.removeEventListener("hashchange", enforce);
  }, [user, logout]);

  // Auto-dismiss the success toast after 4 seconds.
  React.useEffect(() => {
    if (!manualCPToast) return undefined;
    const t = setTimeout(() => setManualCPToast(""), 4000);
    return () => clearTimeout(t);
  }, [manualCPToast]);

  // Step 7.6 (2026-02-15) — Hooks that live at the top of the function so
  // React's rules-of-hooks stay satisfied when we early-return below.
  // `enabled` gates whether the idle timer actually runs; unauthenticated
  // / gated states pass `false` so no listeners attach and no timeouts
  // fire until the user is fully signed in.
  //
  // 2026-02-15 (Dave — "Keep me logged in doesn't keep me logged in") —
  // when the current session is persistent (token lives in localStorage
  // because the user checked "Keep me logged in on this device"), we do
  // NOT arm the idle-logout timer. Otherwise the 30-minute idle default
  // silently defeats the 14-day remember-me contract: walk away for
  // coffee, come back to a login screen. Ephemeral sessions (unchecked)
  // still get the idle guard so shared-machine users aren't left signed
  // in. Read fresh each render so a subsequent login flips the gating
  // correctly without needing a full reload.
  const { settings } = useSettings();
  const persistentSession = isRememberedDevice();
  const authedAndPastGates =
    !!user &&
    user !== null &&
    user.must_change_password !== true &&
    user.sop_acknowledged_at !== null;
  useActivityHeartbeat({ url: `${API}/auth/heartbeat`, enabled: authedAndPastGates });
  const { warningLeft, staySignedIn } = useSessionIdle({
    idleMinutes: Number(settings.session_idle_minutes ?? 30),
    warningSeconds: Number(settings.session_expiring_warning_seconds ?? 60),
    onExpire: logout,
    enabled: authedAndPastGates && !persistentSession,
  });

  // 2026-10-10 (Dave) — Absolute sign-in limit: warn 5 min before the JWT
  // expires, then sign out cleanly at expiry (no stale screens left behind).
  const [expLeft, setExpLeft] = React.useState(null);
  React.useEffect(() => {
    if (!authedAndPastGates || !tokenExp) {
      setExpLeft(null);
      return undefined;
    }
    const tick = () => {
      const left = Math.round((tokenExp - Date.now()) / 1000);
      if (left <= 0) {
        setExpLeft(null);
        expireSession();
        return;
      }
      setExpLeft(left <= 300 ? left : null);
    };
    tick();
    const t = setInterval(tick, 1000);
    return () => clearInterval(t);
  }, [tokenExp, authedAndPastGates, expireSession]);

  // 2026-09-27 (Dave) — Platform kill-switch state poller (v2). Refresh
  // once on mount, then every 30 s, plus on-demand when the Admin
  // modal fires `ureserve:platform-state-changed`. Server returns a
  // computed `phase` ('off'|'scheduled'|'active'|'expired' — the last
  // auto-lifts server-side on read); the frontend renders on that.
  //   • phase='active' + non-admin → full MaintenancePage
  //   • phase='scheduled'          → coral banner strip (≤15 min lead)
  //   • phase='off'                → nothing
  // Poll cadence tightens to 10 s during scheduled/active so users
  // recover within seconds of the switch flipping OFF.
  const [platformState, setPlatformState] = React.useState(null);
  React.useEffect(() => {
    let cancelled = false;
    let timer = null;
    const fetchOnce = () => {
      axios.get(`${API}/platform/state`).then((r) => {
        if (!cancelled) {
          setPlatformState(r.data);
          schedule(r.data?.phase);
        }
      }).catch(() => { /* fail-open: keep last known state */ });
    };
    const schedule = (phase) => {
      if (timer) clearTimeout(timer);
      const ms = (phase === "scheduled" || phase === "active") ? 10_000 : 30_000;
      timer = setTimeout(fetchOnce, ms);
    };
    fetchOnce();
    const onChanged = () => fetchOnce();
    window.addEventListener("ureserve:platform-state-changed", onChanged);
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
      window.removeEventListener("ureserve:platform-state-changed", onChanged);
    };
  }, []);

  // 2026-02-19 (Dave) — reset-with-token modal has priority over every
  // gate below: an active `#reset?token=…` hash means the user is in
  // the middle of a self-service password reset flow. Show the modal
  // regardless of whether they're signed in (they might have been,
  // logged out to click the emailed link, or never had a session at all
  // — all three paths converge on the same UI).
  if (resetTokenActive) {
    return (
      <ResetPasswordTokenModal
        onClose={() => setResetTokenActive(false)}
      />
    );
  }

  // user === null → still loading /me. Render a minimal placeholder to
  // avoid a flash of LoginScreen for already-authenticated users.
  // 2026-02-25 (Dave) — match the post-login boot spinner (sky spinner
  // + navy "Loading uReserve…" text) so the app has a single consistent
  // startup look whether the visitor is signed in or not.
  if (user === null) {
    return (
      <div
        className="min-h-screen flex items-center justify-center"
        style={{ background: "#FAFAF9" }}
        data-testid="app-preauth-spinner"
      >
        <div
          className="flex items-center tracking-wide"
          style={{
            fontSize: 30,
            fontWeight: 700,
            transform: "translateY(-64px)",
            // 2026-03-01 (Dave) — gap tuned 22 → 16 px after the
            // +25 % spinner bump so the pair reads as a compact unit.
            gap: 16,
            // 2026-02-27 (Dave) — mirror the pre-hydration spinner
            // font stack (see index.html and comment on the twin
            // spinner above) so React's mount doesn't visibly swap
            // fonts and pop the label larger→smaller in Chrome.
            fontFamily:
              'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
          }}
        >
          <span
            className="inline-block rounded-full animate-spin"
            style={{
              // 2026-03-01 (Dave) — +25 %: 28→35 px, border 5→6 px,
              // locked in pixels so pre-hydration and post-hydration
              // spinners are visually identical across the mount swap.
              width: 35,
              height: 35,
              border: "6px solid #0091D5",
              borderTopColor: "#EA6A47",
            }}
            aria-hidden="true"
          />
          <span>
            <span style={{ color: "#A8B3BF", fontSize: 26 }}>loading </span>
            <span style={{ color: "#1C4E80" }}>u<em>Reserve</em> Planning</span>
          </span>
        </div>
      </div>
    );
  }
  if (!user) {
    // Wait until the anonymous settings fetch resolves so we never
    // flash the wrong screen for a half-second on cold load.
    if (webEnrollEnabled === undefined) {
      return null;
    }
    // 2026-02-28 (Dave) — Step 5 landing.  Anonymous visitors default
    // to the marketing landing page WHEN the web-enrollment toggle
    // is ON.  Explicit sign-in flows (login, invite, forgot password
    // reset) still route to LoginScreen so the invite hash pre-fill
    // and the forgot-password reset flow remain untouched.
    // `landingHashTick` is a state flag that AppGate flips whenever
    // `hashchange` fires, so pressing the "Sign In" link on the
    // landing page (which just mutates the hash) re-runs this branch
    // and lands on the login screen.
    const rawHash = (typeof window !== "undefined" ? window.location.hash : "") || "";
    const wantsLogin = (
      rawHash.startsWith("#login") ||
      rawHash.startsWith("#reset?token=") ||
      forgotOpen ||
      resetTokenActive
    );
    const wantsEnroll = rawHash.startsWith("#enroll");
    if (rawHash.startsWith("#subscribe")) {
      return <SubscribeInviteScreen user={null} />;
    }
    // When the toggle is OFF: default to LoginScreen, but keep
    // `#enroll` reachable so admins can preview the flow without
    // exposing it publicly.  When ON: landing is the default.
    if (wantsEnroll && !wantsLogin) {
      return <EnrollScreen />;
    }
    if (webEnrollEnabled && !wantsLogin) {
      return <LandingScreen />;
    }
    return (
      <>
        <LoginScreen />
        {forgotOpen && (
          <ForgotPasswordModal
            initialEmail={forgotOpen.email || ""}
            onClose={() => setForgotOpen(false)}
          />
        )}
      </>
    );
  }

  // 2026-09-27 (Dave) — Platform kill-switch gate. Authenticated
  // non-Platform-Admins land here only during the `active` phase
  // (start_at ≤ now < back_at). During `scheduled` they keep working
  // and see the coral MaintenanceBanner instead. Platform Admins
  // never get gated — they need to reach Admin → Maintenance to
  // manage the switch.
  if (platformState?.phase === "active" && !isPlatformAdmin(user)) {
    return (
      <MaintenancePage
        startAt={platformState.start_at}
        backAt={platformState.back_at}
        note={platformState.note}
        serverNow={platformState.server_now}
      />
    );
  }

  // Step 6.75 (2026-02-15) — Force password change on first login.
  // Runs BEFORE the SOP gate — a rotating credential is the tighter
  // security surface, so we resolve it first. Same "explicit === true"
  // guard the SOP gate uses so that an undefined flag during the
  // /auth/login → /auth/me hydration window doesn't flash the modal.
  const needsPasswordChange = user.must_change_password === true;
  const handleChangePassword = async (curPw, newPw) => {
    try {
      setPwError("");
      await changePassword(curPw, newPw);
    } catch (e) {
      const msg = e?.message || "Could not change your password.";
      setPwError(msg);
      throw e; // let the modal keep the fields open + surface error
    }
  };

  // Step 6.5 (2026-08-02) — Force Statement of Purpose acknowledgement on
  // first login. Renders the Shell in the background (so users see the
  // app they're about to unlock) but blocks all interaction with a
  // full-viewport modal that only dismisses on click of "Reviewed and
  // Acknowledged". If the acknowledge POST fails, we surface the error
  // inside the modal AND offer a "Sign out" escape so the user is
  // never fully trapped.
  //
  // 2026-08-03 fix — Dave reported a "flash" on re-login. Root cause:
  // the lean /auth/login response doesn't include sop_acknowledged_at,
  // so it was `undefined` for ~200 ms between login and the /auth/me
  // hydration. `!user.sop_acknowledged_at` treated undefined as
  // "unacknowledged" → modal briefly rendered, then dismissed once
  // /auth/me came back with the ISO string. Switching to an explicit
  // `=== null` check means only the server-confirmed unacknowledged
  // state opens the modal; undefined is treated as "unknown, wait".
  const needsSop = !needsPasswordChange && user.sop_acknowledged_at === null;
  const handleAck = async () => {
    try {
      setAckError("");
      await acknowledgeSop();
    } catch (e) {
      setAckError(e?.message || "Could not record your acknowledgement. Please try again.");
    }
  };

  // Step 7.6 (2026-02-15) — Idle-timeout warning. The hook itself lives
  // at the top of AppGate (rules-of-hooks); this comment marks where
  // its return values are consumed in the render tree below.

  // 2026-03 (Dave) — Context-sensitive Reserve Budget Report short-circuit.
  // The Association-tab pill opens `#report/reserve-budget/{aid}` in a
  // new tab. When we detect that hash we render ONLY the report — no
  // TopBar, no ControlsStrip, no Shell — so the tab reads as a
  // standalone document (behaviour of the old static HTML file it
  // replaced).
  const reportHash = (typeof window !== "undefined" ? window.location.hash : "") || "";
  // Subscription invite for an existing association — resumes after sign-in via sessionStorage.
  if (!needsPasswordChange && !needsSop && (reportHash.startsWith("#subscribe") || sessionStorage.getItem(PENDING_SUBSCRIBE_KEY))) {
    return <SubscribeInviteScreen user={user} onSignOut={logout} />;
  }
  const reportPrefix = "#report/reserve-budget/";
  if (reportHash.startsWith(reportPrefix)) {
    const aid = reportHash.slice(reportPrefix.length).split(/[?/#]/)[0];
    return (
      <>
        <ReserveBudgetReport aid={aid} />
        {/* 2026-02-27 (evening, Dave) — Report route short-circuits
            before the main app <Toaster/> mounts. Attach a local one
            so ReserveBudgetReport's "Preparing your report…" progress
            toast (and its error toast) surface for users. */}
        <Toaster position="top-center" richColors closeButton />
      </>
    );
  }

  return (
    <>
      <Shell platformState={platformState} />
      {needsPasswordChange && (
        <ChangePasswordModal
          open
          mode="forced"
          onSubmit={handleChangePassword}
          errorMessage={pwError}
          onSignOut={logout}
        />
      )}
      {/* Manual (user-initiated) change-password. Suppressed while the
          forced modal is up so the two never overlap. */}
      {!needsPasswordChange && manualCPOpen && (
        <ChangePasswordModal
          open
          mode="manual"
          onSubmit={handleChangePassword}
          onSuccess={() => {
            setManualCPOpen(false);
            setPwError("");
            setManualCPToast("Password changed. Other devices have been signed out.");
          }}
          onClose={() => { setManualCPOpen(false); setPwError(""); }}
          errorMessage={pwError}
        />
      )}
      {/* 2026-02-19 (Dave) — self-service edit-name modal. Refreshes
          AuthContext.user on save so the new name shows up everywhere
          without a page reload. */}
      <EditNameModal
        open={editNameOpen}
        currentName={user?.full_name || ""}
        onClose={() => setEditNameOpen(false)}
        onSaved={() => { refresh(); }}
      />
      {/* Success toast for manual change-password. Fixed at top-center,
          navy pill, auto-dismisses via the effect above. */}
      {manualCPToast && (        <div
          className="fixed top-4 left-1/2 -translate-x-1/2 z-[60] bg-navy text-white text-[12px] px-4 py-2.5 rounded-[6px] shadow-lg border border-navy"
          data-testid="change-password-success-toast"
          role="status"
        >
          {manualCPToast}
        </div>
      )}
      {needsSop && (
        <StatementOfPurposeModal
          open
          onAcknowledge={handleAck}
          errorMessage={ackError}
          onSignOut={logout}
        />
      )}
      {warningLeft != null ? (
        <SessionExpiringToast secondsLeft={warningLeft} onStay={staySignedIn} />
      ) : (
        <SessionExpiringToast
          secondsLeft={expLeft}
          onStay={logout}
          title="Your sign-in ends in"
          subtitle="For security, sign-ins have a time limit. Save your work, then sign in again."
          buttonLabel="Sign in now"
          testIdPrefix="session-limit"
        />
      )}
      <Toaster position="top-center" richColors closeButton />
    </>
  );
}
